import {
  createSlice,
  type AnyAction,
  type Middleware,
  type MiddlewareAPI,
  type PayloadAction,
} from '@reduxjs/toolkit';

export type RtkFetchSideLaneMode = 'off' | 'shadow' | 'seed';
export type RtkFetchSideLaneStatus = 'pending' | 'fulfilled' | 'rejected';

export type RtkFetchSideLaneRouteContext = {
  routeId: string;
  routeFamily?: string;
  pathname?: string;
  personaScope?: string;
};

export type RtkFetchSideLaneRouteMatch = {
  routeId?: string;
  pathname?: string;
  pattern?: string;
};

export type RtkFetchSideLaneOperationMatcher = {
  apiId: string;
  endpointName: string;
  operation?: string;
  facet?: string;
};

export type RtkFetchSideLaneOperationRef = {
  key: string;
  policyId: string;
  routeFamily: string;
  routeId: string;
  pathname: string | null;
  personaScope: string | null;
  apiId: string;
  endpointName: string;
  operation: string | null;
  facet: string | null;
  argsHash: string;
  queryCacheKey: string | null;
  requestId: string | null;
  status: RtkFetchSideLaneStatus;
  firstObservedAt: string;
  lastObservedAt: string;
};

export type RtkFetchSideLaneSeedContext = {
  source: ResolvedRtkQueryAction;
  policy: RtkFetchSideLanePolicy;
};

export type RtkFetchSideLaneSeedProjection = {
  args: unknown;
  payload: unknown;
};

export type RtkFetchSideLaneSeedRule = {
  ruleId: string;
  source: RtkFetchSideLaneOperationMatcher;
  target: RtkFetchSideLaneOperationMatcher;
  project?: (context: RtkFetchSideLaneSeedContext) => RtkFetchSideLaneSeedProjection | null;
};

export type RtkFetchSideLaneTreeProjectorContext = {
  read: (matcher: RtkFetchSideLaneOperationMatcher, args: unknown) => unknown;
  getState: () => unknown;
  references: readonly RtkFetchSideLaneOperationRef[];
};

export type RtkFetchSideLanePolicy = {
  policyId: string;
  routeFamily: string;
  staffRoutes: readonly (string | RtkFetchSideLaneRouteMatch)[];
  observedOperations: readonly RtkFetchSideLaneOperationMatcher[];
  seedRules?: readonly RtkFetchSideLaneSeedRule[];
  treeProjector?: (context: RtkFetchSideLaneTreeProjectorContext) => Record<string, unknown>;
};

export type RtkFetchSideLaneMetricEvent = {
  type: 'observe' | 'seed-attempt' | 'seed-success' | 'seed-skip';
  policyId: string;
  routeFamily: string;
  routeId: string;
  apiId: string;
  endpointName: string;
  operation: string | null;
  status?: RtkFetchSideLaneStatus;
  ruleId?: string;
  reason?: string;
  at: string;
};

export type RtkFetchSideLaneInstrumentation = {
  record: (event: RtkFetchSideLaneMetricEvent) => void;
};

export type RtkFetchSideLaneRedactArgs = (args: unknown) => string;

export type RtkFetchSideLaneApiAdapter = {
  select?: (endpointName: string, args: unknown) => (state: unknown) => unknown;
  upsertQueryData?: (endpointName: string, args: unknown, payload: unknown) => unknown;
};

export type RtkFetchSideLaneMiddlewareOptions = {
  mode: RtkFetchSideLaneMode;
  policies: readonly RtkFetchSideLanePolicy[];
  routeSelector: () => RtkFetchSideLaneRouteContext | null;
  redactArgs?: RtkFetchSideLaneRedactArgs;
  instrumentation?: RtkFetchSideLaneInstrumentation;
  apiAdapters?: Record<string, RtkFetchSideLaneApiAdapter>;
};

export type RtkFetchSideLaneProxyOptions = {
  policies: readonly RtkFetchSideLanePolicy[];
  apiAdapters: Record<string, RtkFetchSideLaneApiAdapter>;
  getState: () => unknown;
  references?: readonly RtkFetchSideLaneOperationRef[];
};

export type RtkFetchSideLaneState = {
  references: Record<string, RtkFetchSideLaneOperationRef>;
  referenceOrder: string[];
  counters: {
    observed: number;
    fulfilled: number;
    rejected: number;
    seedAttempts: number;
    seedSuccesses: number;
    seedSkips: number;
  };
};

type RecordOperationPayload = RtkFetchSideLaneOperationRef;

type RecordSeedPayload = {
  outcome: 'attempt' | 'success' | 'skip';
};

type ResolvedRtkQueryAction = {
  raw: AnyAction;
  apiId: string;
  endpointName: string;
  operation: string | null;
  args: unknown;
  argsHash: string;
  queryCacheKey: string | null;
  requestId: string | null;
  status: RtkFetchSideLaneStatus;
  payload: unknown;
};

const SLICE_NAME = 'rtkFetchSideLane';
const MAX_REFERENCES = 500;

const initialState: RtkFetchSideLaneState = {
  references: {},
  referenceOrder: [],
  counters: {
    observed: 0,
    fulfilled: 0,
    rejected: 0,
    seedAttempts: 0,
    seedSuccesses: 0,
    seedSkips: 0,
  },
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const stableStringify = (value: unknown): string => {
  if (value == null) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `[${value.map((entry) => stableStringify(entry)).join(',')}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(String(value));
};

export const hashRtkFetchSideLaneArgs = (value: unknown): string => {
  const input = stableStringify(value);
  let hash = 5381;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 33) ^ input.charCodeAt(index);
  }
  return `h${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

const defaultRedactArgs: RtkFetchSideLaneRedactArgs = hashRtkFetchSideLaneArgs;

const normalizePattern = (pattern: string): RegExp => {
  let expression = '';
  for (let index = 0; index < pattern.length; index += 1) {
    const char = pattern[index];
    if (char === ':') {
      while (index + 1 < pattern.length && /[A-Za-z0-9_]/.test(pattern[index + 1] ?? '')) {
        index += 1;
      }
      expression += '[^/]+';
      continue;
    }
    if (char === '{') {
      const closeIndex = pattern.indexOf('}', index + 1);
      if (closeIndex > index) {
        const alternatives = pattern
          .slice(index + 1, closeIndex)
          .split('|')
          .map((entry) => entry.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
          .join('|');
        expression += `(?:${alternatives})`;
        index = closeIndex;
        continue;
      }
    }
    expression += char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  return new RegExp(`^${expression}/?$`);
};

const routeMatches = (
  routeContext: RtkFetchSideLaneRouteContext,
  candidate: string | RtkFetchSideLaneRouteMatch,
): boolean => {
  if (typeof candidate === 'string') {
    return routeContext.routeId === candidate || routeContext.pathname === candidate;
  }

  if (candidate.routeId && routeContext.routeId === candidate.routeId) {
    return true;
  }
  if (candidate.pathname && routeContext.pathname === candidate.pathname) {
    return true;
  }
  if (candidate.pattern && routeContext.pathname) {
    return normalizePattern(candidate.pattern).test(routeContext.pathname);
  }
  return false;
};

const operationMatches = (
  matcher: RtkFetchSideLaneOperationMatcher,
  resolved: Pick<ResolvedRtkQueryAction, 'apiId' | 'endpointName' | 'operation'>,
): boolean =>
  matcher.apiId === resolved.apiId &&
  matcher.endpointName === resolved.endpointName &&
  (matcher.operation == null || matcher.operation === resolved.operation);

const getActionStatus = (action: AnyAction): RtkFetchSideLaneStatus | null => {
  const type = toText(action.type);
  if (!type) return null;
  if (type.endsWith('/pending')) return 'pending';
  if (type.endsWith('/fulfilled')) return 'fulfilled';
  if (type.endsWith('/rejected')) return 'rejected';
  return null;
};

const resolveRtkQueryAction = (
  action: unknown,
  redactArgs: RtkFetchSideLaneRedactArgs,
): ResolvedRtkQueryAction | null => {
  if (!isRecord(action)) return null;
  const raw = action as AnyAction;
  const type = toText(raw.type);
  const status = getActionStatus(raw);
  if (!type || !status) return null;

  const typeParts = type.split('/');
  if (typeParts.length < 3 || typeParts[1] !== 'executeQuery') {
    return null;
  }

  const apiId = typeParts[0];
  const meta = isRecord(raw.meta) ? raw.meta : {};
  const metaArg = isRecord(meta.arg) ? meta.arg : {};
  const endpointName = toText(metaArg.endpointName);
  if (!endpointName) return null;

  const args = metaArg.originalArgs;
  const operation = endpointName === 'domainQuery' && isRecord(args)
    ? toText(args.operation)
    : null;

  return {
    raw,
    apiId,
    endpointName,
    operation,
    args,
    argsHash: redactArgs(args),
    queryCacheKey: toText(metaArg.queryCacheKey),
    requestId: toText(meta.requestId),
    status,
    payload: raw.payload,
  };
};

const findPolicies = (
  policies: readonly RtkFetchSideLanePolicy[],
  routeContext: RtkFetchSideLaneRouteContext,
  resolved: ResolvedRtkQueryAction,
): RtkFetchSideLanePolicy[] =>
  policies.filter((policy) =>
    policy.staffRoutes.some((route) => routeMatches(routeContext, route)) &&
    policy.observedOperations.some((operation) => operationMatches(operation, resolved)));

const findFacet = (
  policy: RtkFetchSideLanePolicy,
  resolved: ResolvedRtkQueryAction,
): string | null =>
  policy.observedOperations.find((operation) => operationMatches(operation, resolved))?.facet ?? null;

const buildReferenceKey = (
  policy: RtkFetchSideLanePolicy,
  routeContext: RtkFetchSideLaneRouteContext,
  resolved: ResolvedRtkQueryAction,
): string =>
  [
    policy.policyId,
    routeContext.routeId,
    resolved.apiId,
    resolved.endpointName,
    resolved.operation ?? 'endpoint',
    resolved.argsHash,
  ].join('|');

const toOperationRef = (
  policy: RtkFetchSideLanePolicy,
  routeContext: RtkFetchSideLaneRouteContext,
  resolved: ResolvedRtkQueryAction,
  previous?: RtkFetchSideLaneOperationRef,
): RtkFetchSideLaneOperationRef => {
  const now = new Date().toISOString();
  return {
    key: buildReferenceKey(policy, routeContext, resolved),
    policyId: policy.policyId,
    routeFamily: policy.routeFamily,
    routeId: routeContext.routeId,
    pathname: routeContext.pathname ?? null,
    personaScope: routeContext.personaScope ?? null,
    apiId: resolved.apiId,
    endpointName: resolved.endpointName,
    operation: resolved.operation,
    facet: findFacet(policy, resolved),
    argsHash: resolved.argsHash,
    queryCacheKey: resolved.queryCacheKey,
    requestId: resolved.requestId,
    status: resolved.status,
    firstObservedAt: previous?.firstObservedAt ?? now,
    lastObservedAt: now,
  };
};

export const defineRtkFetchSideLanePolicy = (
  policy: RtkFetchSideLanePolicy,
): RtkFetchSideLanePolicy => policy;

export const createRtkFetchSideLaneSlice = () =>
  createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {
      recordOperation(state, action: PayloadAction<RecordOperationPayload>) {
        const nextRef = action.payload;
        const previous = state.references[nextRef.key];
        state.references[nextRef.key] = {
          ...nextRef,
          firstObservedAt: previous?.firstObservedAt ?? nextRef.firstObservedAt,
        };
        if (!previous) {
          state.referenceOrder.push(nextRef.key);
          if (state.referenceOrder.length > MAX_REFERENCES) {
            const expired = state.referenceOrder.splice(0, state.referenceOrder.length - MAX_REFERENCES);
            for (const key of expired) {
              delete state.references[key];
            }
          }
        }
        state.counters.observed += 1;
        if (nextRef.status === 'fulfilled') state.counters.fulfilled += 1;
        if (nextRef.status === 'rejected') state.counters.rejected += 1;
      },
      recordSeed(state, action: PayloadAction<RecordSeedPayload>) {
        if (action.payload.outcome === 'attempt') state.counters.seedAttempts += 1;
        if (action.payload.outcome === 'success') state.counters.seedSuccesses += 1;
        if (action.payload.outcome === 'skip') state.counters.seedSkips += 1;
      },
    },
  });

const sharedSlice = createRtkFetchSideLaneSlice();

export const rtkFetchSideLaneReducer = sharedSlice.reducer;
export const rtkFetchSideLaneActions = sharedSlice.actions;

const recordMetric = (
  instrumentation: RtkFetchSideLaneInstrumentation | undefined,
  event: RtkFetchSideLaneMetricEvent,
) => {
  instrumentation?.record(event);
};

const buildTargetArgs = (
  target: RtkFetchSideLaneOperationMatcher,
  sourceArgs: unknown,
): unknown => {
  if (target.endpointName === 'domainQuery' && target.operation) {
    return { operation: target.operation, args: isRecord(sourceArgs) ? sourceArgs.args : sourceArgs };
  }
  return sourceArgs;
};

const applySeedRules = (
  api: MiddlewareAPI,
  options: RtkFetchSideLaneMiddlewareOptions,
  policy: RtkFetchSideLanePolicy,
  routeContext: RtkFetchSideLaneRouteContext,
  resolved: ResolvedRtkQueryAction,
) => {
  for (const rule of policy.seedRules ?? []) {
    if (!operationMatches(rule.source, resolved)) continue;

    const baseEvent = {
      policyId: policy.policyId,
      routeFamily: policy.routeFamily,
      routeId: routeContext.routeId,
      apiId: resolved.apiId,
      endpointName: resolved.endpointName,
      operation: resolved.operation,
      at: new Date().toISOString(),
      ruleId: rule.ruleId,
    };
    api.dispatch(rtkFetchSideLaneActions.recordSeed({ outcome: 'attempt' }));
    recordMetric(options.instrumentation, { ...baseEvent, type: 'seed-attempt' });

    const adapter = options.apiAdapters?.[rule.target.apiId];
    if (!adapter?.upsertQueryData) {
      api.dispatch(rtkFetchSideLaneActions.recordSeed({ outcome: 'skip' }));
      recordMetric(options.instrumentation, {
        ...baseEvent,
        type: 'seed-skip',
        reason: 'missing-target-adapter',
      });
      continue;
    }

    const projection = rule.project
      ? rule.project({ source: resolved, policy })
      : {
          args: buildTargetArgs(rule.target, resolved.args),
          payload: resolved.payload,
        };

    if (!projection) {
      api.dispatch(rtkFetchSideLaneActions.recordSeed({ outcome: 'skip' }));
      recordMetric(options.instrumentation, {
        ...baseEvent,
        type: 'seed-skip',
        reason: 'projector-returned-null',
      });
      continue;
    }

    api.dispatch(adapter.upsertQueryData(rule.target.endpointName, projection.args, projection.payload) as AnyAction);
    api.dispatch(rtkFetchSideLaneActions.recordSeed({ outcome: 'success' }));
    recordMetric(options.instrumentation, { ...baseEvent, type: 'seed-success' });
  }
};

export const createRtkFetchSideLaneMiddleware = (
  options: RtkFetchSideLaneMiddlewareOptions,
): Middleware => {
  const redactArgs = options.redactArgs ?? defaultRedactArgs;

  return (api) => (next) => (action) => {
    if (options.mode === 'off') {
      return next(action);
    }

    const resolved = resolveRtkQueryAction(action, redactArgs);
    if (!resolved) {
      return next(action);
    }

    const routeContext = options.routeSelector();
    if (!routeContext) {
      return next(action);
    }

    const matchedPolicies = findPolicies(options.policies, routeContext, resolved);
    if (matchedPolicies.length === 0) {
      return next(action);
    }

    const result = next(action);

    for (const policy of matchedPolicies) {
      const key = buildReferenceKey(policy, routeContext, resolved);
      const state = api.getState() as { rtkFetchSideLane?: RtkFetchSideLaneState };
      const reference = toOperationRef(policy, routeContext, resolved, state.rtkFetchSideLane?.references[key]);
      api.dispatch(rtkFetchSideLaneActions.recordOperation(reference));
      recordMetric(options.instrumentation, {
        type: 'observe',
        policyId: policy.policyId,
        routeFamily: policy.routeFamily,
        routeId: routeContext.routeId,
        apiId: resolved.apiId,
        endpointName: resolved.endpointName,
        operation: resolved.operation,
        status: resolved.status,
        at: reference.lastObservedAt,
      });

      if (options.mode === 'seed' && resolved.status === 'fulfilled') {
        applySeedRules(api, options, policy, routeContext, resolved);
      }
    }

    return result;
  };
};

export const createRtkFetchSideLaneProxy = ({
  policies,
  apiAdapters,
  getState,
  references = [],
}: RtkFetchSideLaneProxyOptions): Record<string, unknown> => {
  const read = (matcher: RtkFetchSideLaneOperationMatcher, args: unknown): unknown => {
    const adapter = apiAdapters[matcher.apiId];
    if (!adapter?.select) return undefined;
    const selectorArgs = matcher.endpointName === 'domainQuery' && matcher.operation
      ? { operation: matcher.operation, args }
      : args;
    const selection = adapter.select(matcher.endpointName, selectorArgs)(getState());
    if (isRecord(selection) && 'data' in selection) return selection.data;
    return selection;
  };

  return policies.reduce<Record<string, unknown>>((tree, policy) => {
    if (!policy.treeProjector) return tree;
    tree[policy.routeFamily] = policy.treeProjector({
      read,
      getState,
      references,
    });
    return tree;
  }, {});
};
